// Zod schemas for the email admin SPA's data-plane resources.
//
// Shared between apps/aira-email (frontend, validates server responses) and
// apps/aira-rag-backend (server, validates request bodies and writes typed
// rows). Schemas mirror the email-app's supabase migrations under
// apps/aira-email/supabase/migrations/ — which are byte-identical to the
// backend's migrations in apps/aira-rag-backend/migrations/ for these tables,
// so the schemas serve as the single source of truth for column shape.
import { z } from 'zod';
const Uuid = z.string().uuid();
const Iso = z.string().min(1);
/** node-pg returns Postgres bigint columns as strings; accept number or integer string. */
export function coerceOptionalIntFromDb(value) {
    if (value === null || value === undefined)
        return value;
    if (typeof value === 'number')
        return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '')
            return null;
        const n = Number.parseInt(trimmed, 10);
        return Number.isNaN(n) ? value : n;
    }
    return value;
}
const OptionalIntFromDb = z.preprocess(coerceOptionalIntFromDb, z.number().int().nullable().optional());
// ── intents ────────────────────────────────────────────────────────────────
export const IntentStatusSchema = z.enum(['draft', 'published']);
export const IntentSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    name: z.string(),
    display_name: z.string(),
    description: z.string(),
    status: IntentStatusSchema,
    required_entities: z.array(z.string()),
    confidence_threshold: z.number().min(0).max(1),
    low_confidence_template_id: z.string().nullable(),
    clarification_template_id: z.string().nullable(),
    is_system: z.boolean(),
    user_id: Uuid,
    updated_by: z.string(),
    created_at: Iso,
    updated_at: Iso,
});
export const IntentCreateSchema = IntentSchema.pick({
    name: true,
    display_name: true,
    description: true,
    status: true,
    required_entities: true,
    confidence_threshold: true,
    low_confidence_template_id: true,
    clarification_template_id: true,
}).partial({
    description: true,
    status: true,
    required_entities: true,
    confidence_threshold: true,
    low_confidence_template_id: true,
    clarification_template_id: true,
});
export const IntentUpdateSchema = IntentCreateSchema.partial();
// ── intent_few_shot_examples ───────────────────────────────────────────────
export const IntentFewShotExampleSchema = z.object({
    id: Uuid,
    intent_id: Uuid,
    sort_order: z.number().int(),
    email: z.string(),
    expected_intent: z.string(),
    expected_slots: z.record(z.unknown()),
    created_at: Iso,
});
export const IntentFewShotExampleCreateSchema = IntentFewShotExampleSchema.pick({
    intent_id: true,
    sort_order: true,
    email: true,
    expected_intent: true,
    expected_slots: true,
}).partial({ sort_order: true, expected_slots: true });
// ── context_dimensions + values ────────────────────────────────────────────
export const ContextDimensionSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    slug: z.string(),
    display_name: z.string(),
    description: z.string(),
    resolution_source: z.enum(['catalog', 'order', 'location', 'classifier', 'admin', 'clock']),
    resolution_field: z.string(),
    required: z.boolean(),
    default_value_slug: z.string().nullable(),
    enabled: z.boolean(),
    sort_order: z.number().int(),
    created_at: Iso,
    updated_at: Iso,
});
export const ContextDimensionCreateSchema = z.object({
    slug: z.string().min(1),
    display_name: z.string().min(1),
    description: z.string().optional(),
    resolution_source: z
        .enum(['catalog', 'order', 'location', 'classifier', 'admin', 'clock'])
        .optional(),
    resolution_field: z.string().optional(),
    required: z.boolean().optional(),
    default_value_slug: z.string().optional(),
    enabled: z.boolean().optional(),
    sort_order: z.number().int().optional(),
});
export const ContextDimensionValueSchema = z.object({
    id: Uuid,
    dimension_id: Uuid,
    value_slug: z.string(),
    display_name: z.string(),
    sort_order: z.number().int(),
    created_at: Iso,
});
export const ContextDimensionValueCreateSchema = z.object({
    dimension_id: Uuid,
    value_slug: z.string().min(1),
    display_name: z.string().min(1),
    sort_order: z.number().int().optional(),
});
// ── prompt_templates ───────────────────────────────────────────────────────
export const TemplateStatusSchema = z.enum(['draft', 'published', 'archived']);
export const TemplateKindSchema = z.enum(['primary', 'clarification', 'escalation']);
export const TemplateResponseStyleSchema = z.enum(['formal', 'casual', 'short', 'long']);
export const TemplateLanguageHintSchema = z.enum(['norwegian', 'english', 'mixed']);
export const PromptTemplateSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    intent_slug: z.string(),
    version: z.number().int().min(1),
    content: z.string(),
    response_style: TemplateResponseStyleSchema,
    language_hint: TemplateLanguageHintSchema,
    status: TemplateStatusSchema,
    banned_phrases_check: z.boolean(),
    required_placeholders: z.array(z.string()),
    kind: TemplateKindSchema,
    user_id: Uuid,
    updated_by: z.string(),
    created_at: Iso,
    updated_at: Iso,
});
export const PromptTemplateCreateSchema = z.object({
    intent_slug: z.string().min(1),
    version: z.number().int().min(1).optional(),
    content: z.string().optional(),
    response_style: TemplateResponseStyleSchema.optional(),
    language_hint: TemplateLanguageHintSchema.optional(),
    status: TemplateStatusSchema.optional(),
    banned_phrases_check: z.boolean().optional(),
    required_placeholders: z.array(z.string()).optional(),
    kind: TemplateKindSchema.optional(),
});
export const PromptTemplateUpdateSchema = PromptTemplateCreateSchema.partial();
// ── prompt_bindings ────────────────────────────────────────────────────────
export const PromptBindingKindSchema = z.enum(['primary']);
export const PromptBindingSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    intent_slug: z.string(),
    kind: PromptBindingKindSchema,
    prompt_template_id: Uuid,
    active: z.boolean(),
    user_id: Uuid,
    updated_by: z.string(),
    created_at: Iso,
    updated_at: Iso,
});
export const PromptBindingUpsertSchema = z.object({
    tenant_name: z.string().min(1),
    intent_slug: z.string().min(1),
    kind: PromptBindingKindSchema.default('primary'),
    prompt_template_id: Uuid,
    active: z.boolean().optional(),
});
// ── prompt_binding_history ─────────────────────────────────────────────────
export const PromptBindingHistorySchema = z.object({
    id: Uuid,
    binding_id: Uuid,
    prompt_template_id: Uuid,
    template_version: z.number().int().min(1),
    actor: z.string(),
    publish_note: z.string(),
    eval_run_id: z.string(),
    created_at: Iso,
});
export const PromptBindingHistoryCreateSchema = z.object({
    binding_id: Uuid,
    prompt_template_id: Uuid,
    template_version: z.number().int().min(1),
    publish_note: z.string().optional(),
    eval_run_id: z.string().optional(),
});
// ── intent_routing (and junctions) ─────────────────────────────────────────
export const RoutingStatusSchema = z.enum(['draft', 'published']);
export const RoutingResponseStyleSchema = z.enum(['formal', 'casual', 'short', 'long']);
export const IntentRoutingSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    intent_slug: z.string(),
    prompt_template_id: Uuid,
    response_style: RoutingResponseStyleSchema,
    tool_spec: z.unknown(),
    knowledge_base_enabled: z.boolean(),
    template_selector: z.unknown().nullable(),
    version: z.number().int().min(1),
    status: RoutingStatusSchema,
    user_id: Uuid,
    updated_by: z.string(),
    created_at: Iso,
    updated_at: Iso,
});
export const IntentRoutingUpsertSchema = z.object({
    tenant_name: z.string().min(1),
    intent_slug: z.string().min(1),
    prompt_template_id: Uuid,
    response_style: RoutingResponseStyleSchema.optional(),
    tool_spec: z.unknown().optional(),
    knowledge_base_enabled: z.boolean().optional(),
    template_selector: z.unknown().nullable().optional(),
    version: z.number().int().min(1).optional(),
    status: RoutingStatusSchema.optional(),
    topic_ids: z.array(Uuid).optional(),
    dimension_ids: z.array(Uuid).optional(),
});
// ── orchestration_tools ────────────────────────────────────────────────────
export const OrchestrationToolSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    name: z.string(),
    description: z.string(),
    returns_summary: z.string(),
    read_only: z.boolean(),
    args_json: z.unknown(),
    sort_order: z.number().int(),
    created_at: Iso,
});
// ── topics ─────────────────────────────────────────────────────────────────
export const TopicSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    skill_id: Uuid.nullable(),
    sort_order: z.number().int().nullable(),
    enabled: z.boolean(),
    created_at: Iso,
    updated_at: Iso,
});
export const TopicCreateSchema = TopicSchema.pick({
    name: true,
    description: true,
    skill_id: true,
    sort_order: true,
    enabled: true,
}).partial({ description: true, skill_id: true, sort_order: true, enabled: true });
// ── skills ─────────────────────────────────────────────────────────────────
export const SkillSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    title: z.string(),
    instruction: z.string().nullable(),
    enabled: z.boolean(),
    is_default: z.boolean(),
    icon: z.string().nullable(),
    sort_order: z.number().int().nullable(),
    created_at: Iso,
});
export const SkillCreateSchema = SkillSchema.pick({
    title: true,
    instruction: true,
    enabled: true,
    is_default: true,
    icon: true,
    sort_order: true,
}).partial({ instruction: true, enabled: true, is_default: true, icon: true, sort_order: true });
export const SkillUpdateSchema = SkillCreateSchema.partial();
// ── tenant_settings ────────────────────────────────────────────────────────
export const TenantDraftLanguageSchema = z
    .string()
    .trim()
    .regex(/^[a-z][a-z -]{1,63}$/i);
export const ShopLanguageCodeSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z]{2}$/);
/** Lookback windows the Gmail background-drafts job supports (hours). */
export const GMAIL_BACKGROUND_LOOKBACK_HOURS = [6, 12, 24, 48];
export const GmailBackgroundLookbackHoursSchema = z.union([
    z.literal(6),
    z.literal(12),
    z.literal(24),
    z.literal(48),
]);
/**
 * Filters that narrow which emails the Gmail background job reads. Every
 * ticked filter must match (AND); none ticked = every unread inbox email in
 * the lookback window. GAS turns them into Gmail search terms, so email that
 * doesn't match never leaves the mailbox.
 */
export const GMAIL_BACKGROUND_FILTERS = ['addresses', 'label', 'primary'];
export const GmailBackgroundFilterSchema = z.enum(GMAIL_BACKGROUND_FILTERS);
/** The label filter keeps emails with the label (`include`) or without it (`exclude`). */
export const GMAIL_BACKGROUND_LABEL_MODES = ['include', 'exclude'];
export const GmailBackgroundLabelModeSchema = z.enum(GMAIL_BACKGROUND_LABEL_MODES);
export const GMAIL_BACKGROUND_MAX_ADDRESSES = 5;
export const GMAIL_BACKGROUND_MAX_EXCLUDES = 20;
/**
 * Entries end up inside a Gmail search query, so they are held to a strict
 * shape: no spaces, quotes, brackets or operators. gas/Background.gs re-checks
 * them with the same patterns.
 */
export const GMAIL_BACKGROUND_ADDRESS_RE = /^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;
/** A domain (`shop.com`) or an address (`ola@shop.com`). */
export const GMAIL_BACKGROUND_EXCLUDE_RE = /^([a-z0-9._%+-]+@)?[a-z0-9-]+(\.[a-z0-9-]+)+$/;
/** Gmail label names: letters, digits, spaces, `-`, `_`, `/` (nested labels). */
export const GMAIL_BACKGROUND_LABEL_RE = /^[\p{L}\p{N}][\p{L}\p{N} _/-]{0,63}$/u;
export const GmailBackgroundAddressSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(GMAIL_BACKGROUND_ADDRESS_RE);
export const GmailBackgroundExcludeSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(GMAIL_BACKGROUND_EXCLUDE_RE);
/** '' = no label. */
export const GmailBackgroundLabelSchema = z
    .string()
    .trim()
    .refine((v) => v === '' || GMAIL_BACKGROUND_LABEL_RE.test(v));
export const TenantSettingsSchema = z.object({
    tenant_name: z.string(),
    daily_token_cap: z.number().int().nonnegative(),
    daily_usd_cap: z.coerce.number().nonnegative(),
    monthly_token_cap: z.number().int().nonnegative().nullable(),
    monthly_usd_cap: z.coerce.number().nonnegative().nullable(),
    default_language: TenantDraftLanguageSchema,
    /** Two-letter Sharefox shop locale code; set at activation; read-only in UI. */
    default_language_code: ShopLanguageCodeSchema.nullable().optional(),
    /** IANA timezone from Sharefox site config (set at activation; read-only in UI). */
    default_timezone: z.string().nullable().optional(),
    /** ISO 4217 currency from Sharefox site config (set at activation; read-only in UI). */
    currency: z.string().nullable().optional(),
    /** ISO country code from Sharefox site config (set at activation; read-only in UI). */
    country_code: z.string().nullable().optional(),
    pii_tier: z.enum(['standard', 'high_risk']),
    use_dlp_anonymisation: z.boolean(),
    reranker: z.boolean(),
    use_llamaparse_fallback: z.boolean(),
    /** Max draft-generation requests per minute on the drafts API (staff-managed). */
    rate_limit_cap: z.number().int().min(1),
    /** Gmail add-on prepares drafts in the background for unread inbox threads. */
    gmail_background_drafts_enabled: z.boolean(),
    /** How far back (hours) the background job looks for unread threads. */
    gmail_background_lookback_hours: GmailBackgroundLookbackHoursSchema,
    /** Max threads the background job kicks per run per user. */
    gmail_background_max_threads: z.number().int().min(1).max(25),
    /** Which emails the background job may read. */
    gmail_background_filters: z.array(GmailBackgroundFilterSchema),
    /** Scope `addresses`: email delivered or sent to one of these. */
    gmail_background_addresses: z.array(z.string()),
    /** Scope `label`: email carrying this Gmail label ('' = none). */
    gmail_background_label: z.string(),
    gmail_background_label_mode: GmailBackgroundLabelModeSchema,
    /** Domains or addresses never read, in every scope. */
    gmail_background_exclude: z.array(z.string()),
    updated_by: z.string(),
    created_at: Iso,
    updated_at: Iso,
});
export const TenantSettingsUpdateSchema = z.object({
    daily_token_cap: z.number().int().nonnegative(),
    daily_usd_cap: z.number().nonnegative(),
    monthly_token_cap: z.number().int().nonnegative().nullable(),
    monthly_usd_cap: z.number().nonnegative().nullable(),
    pii_tier: z.enum(['standard', 'high_risk']),
    use_dlp_anonymisation: z.boolean(),
    reranker: z.boolean(),
    use_llamaparse_fallback: z.boolean(),
    /**
     * Drafts API rate cap (requests/minute). Optional: only staff callers may
     * send it — the backend rejects the field for non-staff, so merchant saves
     * must omit it entirely.
     */
    rate_limit_cap: z.number().int().min(1).optional(),
    /** Optional so clients that predate background drafts keep the stored values. */
    gmail_background_drafts_enabled: z.boolean().optional(),
    gmail_background_lookback_hours: GmailBackgroundLookbackHoursSchema.optional(),
    gmail_background_max_threads: z.number().int().min(1).max(25).optional(),
    gmail_background_filters: z
        .array(GmailBackgroundFilterSchema)
        .max(GMAIL_BACKGROUND_FILTERS.length)
        .refine((v) => new Set(v).size === v.length, 'duplicate filter')
        .optional(),
    gmail_background_addresses: z
        .array(GmailBackgroundAddressSchema)
        .max(GMAIL_BACKGROUND_MAX_ADDRESSES)
        .optional(),
    gmail_background_label: GmailBackgroundLabelSchema.optional(),
    gmail_background_label_mode: GmailBackgroundLabelModeSchema.optional(),
    gmail_background_exclude: z
        .array(GmailBackgroundExcludeSchema)
        .max(GMAIL_BACKGROUND_MAX_EXCLUDES)
        .optional(),
});
// ── sample email products (Assistant Testing wand) ─────────────────────────
/** GET /v1/sample-email-products — the shop's top product names; empty when unavailable. */
export const SampleEmailProductsSchema = z.object({
    product_names: z.array(z.string()),
});
// ── tenant_personality ─────────────────────────────────────────────────────
export const TenantPersonalitySchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    persona_name: z.string().nullable(),
    tone: z.string().nullable(),
    writing_style: z.string().nullable(),
    custom_instructions: z.string().nullable(),
    fallback_response: z.string().nullable(),
    signature: z.string().nullable(),
    merchant_context: z.string().nullable(),
    created_at: Iso,
    updated_at: Iso,
});
export const TenantPersonalityUpsertSchema = TenantPersonalitySchema.pick({
    persona_name: true,
    tone: true,
    writing_style: true,
    custom_instructions: true,
    fallback_response: true,
    signature: true,
    merchant_context: true,
}).partial();
// ── knowledge_entries (read-side; ingestion already lives in knowledge.ts) ──
export const KnowledgeEntryTypeSchema = z.enum(['qa', 'text', 'document']);
export const KnowledgeEntrySchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    user_id: Uuid,
    type: KnowledgeEntryTypeSchema,
    question: z.string().nullable(),
    answer: z.string().nullable(),
    title: z.string().nullable(),
    content: z.string().nullable(),
    topic_id: Uuid.nullable(),
    topic_ids: z.array(Uuid).optional(),
    context_dimension_value_ids: z.array(Uuid).optional(),
    parent_document_id: Uuid.nullable(),
    is_private: z.boolean(),
    original_filename: z.string().nullable(),
    source_type: z.string().nullable().optional(),
    source_url: z.string().nullable().optional(),
    source_id: Uuid.nullish(),
    language_code: z
        .string()
        .regex(/^[a-z]{2}$/)
        .nullable()
        .optional(),
    // Optional on read: columns may be absent until a future migration adds them.
    embedding_type: z.string().nullish(),
    content_hash: z.string().nullish(),
    created_at: Iso,
    updated_at: Iso,
});
export const KnowledgeEntryUpdateSchema = KnowledgeEntrySchema.pick({
    question: true,
    answer: true,
    title: true,
    content: true,
    topic_id: true,
    is_private: true,
}).partial();
// ── draft_history ──────────────────────────────────────────────────────────
/** Source of a draft run — indicates where the request originated. */
export const DraftSourceSchema = z.enum([
    'gmail',
    'outlook',
    'assistant_testing',
    'staff_testing',
    'api',
    // Legacy alias of staff_testing (old Test Sample Emails page) — still accepted on read.
    'test_sample_emails',
]);
export const DraftHistoryKnowledgeEntryUsedSchema = z.object({
    id: z.string(),
    type: z.string(),
    topic_name: z.string().nullish(),
    similarity: z.number().nullish(),
    content_preview: z.string().optional(),
    updated_at: z.string().optional(),
});
/**
 * One row of `draft_history.sources` — a straight copy of the run's
 * `aira_runs.citations` (see apps/aira-rag-backend/src/graph/citations.ts)
 * taken at save time, so merchant Conversations / add-ins render the same
 * curated list: RAG knowledge entries already filtered to >50% match (plus
 * low-confidence hits marked `kind: 'knowledge_low_confidence'` so the UI can
 * surface a "background knowledge consulted" note instead of a detailed
 * citation), plus Sharefox Live API tool sources. Distinct from (and more
 * curated than) `knowledge_entries_used` above, which is the unfiltered
 * GDPR/audit record and what staff All Conversations shows for knowledge
 * hits.
 */
export const DraftHistorySourceSchema = z.object({
    chunk_id: z.string(),
    source: z.string().optional(),
    snippet: z.string().optional(),
    kind: z.enum(['knowledge', 'knowledge_low_confidence', 'live']).optional(),
    similarity: z.number().nullish(),
    entry_id: z.string().optional(),
    entry_type: z.enum(['qa', 'text', 'document']).optional(),
    edit_url: z.string().optional(),
    tool: z.string().optional(),
});
/** knowledge_entries.source_type when promoting conversation training notes. */
export const DRAFT_HISTORY_KB_SOURCE_MERCHANT_TRAINING = 'draft_history_merchant_training';
export const DRAFT_HISTORY_KB_SOURCE_STAFF_TRAINING = 'draft_history_staff_training';
/** Inline promote result returned from PATCH /v1/draft-history/:id when
 *  promote_to_tenant_kb or promote_to_global_kb is true and succeeds. */
export const DraftHistoryPromoteResultSchema = z.object({
    type: z.literal('text'),
    chunks: z.number().int().nonnegative(),
    ids: z.array(Uuid),
    already_promoted: z.boolean().optional(),
});
/** Inline promote error returned from PATCH /v1/draft-history/:id when
 *  promote_to_tenant_kb or promote_to_global_kb is true but fails. */
export const DraftHistoryPromoteErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
});
export const DraftHistorySchema = z.object({
    id: Uuid,
    /** Per-tenant human-readable reference, e.g. "conversation 214". */
    display_number: z.coerce.number().int().positive(),
    tenant_name: z.string(),
    user_id: Uuid,
    /** Orchestration run that produced this draft. Null for rows saved before the
     *  run_id link existed, or when no run could be associated. Enables deep-link
     *  into the pipeline + tool history (Test Sample Emails ?runId=). */
    run_id: Uuid.nullish(),
    original_email_anonymized: z.string(),
    sender_name_anonymized: z.string().nullable(),
    sender_email_anonymized: z.string().nullable(),
    subject_anonymized: z.string().nullable(),
    draft_response_anonymized: z.string(),
    knowledge_entries_used: z.array(DraftHistoryKnowledgeEntryUsedSchema),
    /** See DraftHistorySourceSchema. Empty on rows saved before this column
     *  existed and never backfilled (no linked run). */
    sources: z.array(DraftHistorySourceSchema).default([]),
    merchant_training_notes: z.string().nullable(),
    /** Staff-only; omitted on merchant scope=tenant reads. */
    staff_training_notes: z.string().nullable().optional(),
    share_with_product_team: z.boolean(),
    merchant_training_updated_at: Iso.nullish(),
    staff_training_updated_at: Iso.nullish(),
    merchant_training_shared_at: Iso.nullish(),
    /** True when merchant training for this conversation is already in tenant KB. */
    merchant_training_kb_promoted: z.boolean().optional(),
    /** When the merchant inserted this draft into Gmail compose (null = generated only). */
    draft_inserted_at: Iso.nullish(),
    /** Gmail add-on thumbs: like = helpful, dislike = not helpful. */
    draft_feedback: z.enum(['like', 'dislike']).nullish(),
    draft_feedback_at: Iso.nullish(),
    /** Source of the draft request (gmail, outlook, assistant_testing, staff_testing, api). */
    source: DraftSourceSchema.nullish(),
    created_at: Iso,
    /** Promote result from PATCH when promote_to_tenant_kb/promote_to_global_kb succeeds. */
    promote_result: DraftHistoryPromoteResultSchema.optional(),
    /** Promote error from PATCH when promote_to_tenant_kb/promote_to_global_kb fails. */
    promote_error: DraftHistoryPromoteErrorSchema.optional(),
});
export const MerchantDraftHistoryUpdateSchema = z.object({
    merchant_training_notes: z.string().nullable().optional(),
    share_with_product_team: z.boolean().optional(),
    promote_to_tenant_kb: z.boolean().optional(),
    topic_ids: z.array(Uuid).optional(),
});
/**
 * Add-in thumbs up/down. Feedback is always required; optional `note` is saved
 * to staff_training_notes so the AIRA Product department can review it in
 * All Conversations. Does not touch merchant_training_notes or promote to KB.
 */
export const DraftHistoryMarkFeedbackSchema = z.object({
    run_id: Uuid,
    feedback: z.enum(['like', 'dislike']),
    note: z.string().max(2000).optional(),
});
export const StaffDraftHistoryUpdateSchema = z.object({
    staff_training_notes: z.string().nullable().optional(),
    promote_to_global_kb: z.boolean().optional(),
    topic_ids: z.array(Uuid).optional(),
});
export const DraftHistoryPromoteKnowledgeSchema = z.object({
    target: z.enum(['tenant', 'global']),
    source: z.enum(['merchant_training', 'staff_training']),
    topic_ids: z.array(Uuid).optional(),
});
export const DraftHistoryPromoteKnowledgeResponseSchema = z.object({
    type: z.literal('text'),
    chunks: z.number().int().nonnegative(),
    ids: z.array(Uuid),
    already_promoted: z.boolean().optional(),
});
/** @deprecated Use MerchantDraftHistoryUpdateSchema or StaffDraftHistoryUpdateSchema */
export const DraftHistoryUpdateSchema = MerchantDraftHistoryUpdateSchema;
// ── synced_websites ────────────────────────────────────────────────────────
export const SyncedWebsiteSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    user_id: Uuid,
    base_url: z.string(),
    page_url: z.string(),
    page_title: z.string().nullable(),
    raw_content: z.string().nullable(),
    entries_created: z.coerce
        .number()
        .int()
        .nullable()
        .transform((v) => v ?? 0),
    last_synced_at: Iso,
    created_at: Iso,
});
// ── token_usage_logs ───────────────────────────────────────────────────────
export const TokenUsageLogSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    user_id: Uuid,
    event_type: z.string(),
    tokens_used: z.number().int(),
    created_at: Iso,
});
// ── integration_settings ───────────────────────────────────────────────────
export const IntegrationSettingSchema = z.object({
    id: Uuid,
    tenant_name: z.string(),
    integration_type: z.string(),
    settings: z.record(z.unknown()),
    created_at: Iso,
    updated_at: Iso,
});
export const IntegrationSettingUpsertSchema = z.object({
    integration_type: z.string().min(1),
    settings: z.record(z.unknown()),
});
// ── Sharefox per-tenant integration (backend-only — never expose token to SPA) ─
export const SHAREFOX_ADMIN_API_INTEGRATION_TYPE = 'sharefox_admin_api';
export const EncryptedTokenSchema = z.object({
    ciphertext: z.string(),
    iv: z.string(),
    tag: z.string(),
    algorithm: z.literal('AES-256-GCM'),
});
export const SharefoxIntegrationSettingsSchema = z
    .object({
    /** Value for `x-sharefox-admin-domain` on Sharefox Admin / platform API calls. */
    admin_domain_header: z.string().optional(),
    /** @deprecated Legacy key — use admin_domain_header. */
    admin_domain: z.string().optional(),
    /** Value for `x-sharefox-shop-domain` on Sharefox Public API / api-v2. */
    shop_domain_header: z.string().optional(),
    /** @deprecated Legacy key — use shop_domain_header. */
    shop_domain: z.string().optional(),
    token_encrypted: EncryptedTokenSchema,
    /**
     * `Bearer <old-API opaque token>` for endpoints that reject JWTs (universal-proxy).
     * Never store a JWT here — legacy rows did, so readers must tolerate it.
     */
    authorization: z.string().optional(),
    expires_at: z.string().nullable().optional(),
    sharefox_admin_user_id: OptionalIntFromDb,
    provisioned_at: z.string(),
})
    .refine((v) => !!(v.admin_domain_header ?? v.admin_domain), {
    message: 'admin_domain_header is required',
    path: ['admin_domain_header'],
});
// ── profiles (read-only from the SPA; writes go through /v1/auth/provision) ─
export const ProfileAuthProviderSchema = z.enum(['identity_platform', 'sharefox_admin']);
export const ProfileSchema = z.object({
    id: Uuid,
    email: z.string().email().nullable(),
    full_name: z.string().nullable(),
    tenant_name: z.string(),
    auth_uid: z.string().nullable(),
    auth_provider: ProfileAuthProviderSchema.default('identity_platform'),
    sharefox_admin_user_id: OptionalIntFromDb,
    /** Sharefox site slug; null when the tenant has no Sharefox Site / Admin mapping. */
    sharefox_sitename: z.string().nullable().optional(),
    created_at: Iso,
    updated_at: Iso,
    /** DB flag: when true this profile may see and enable Global Mode. */
    global_access: z.boolean().optional().default(false),
    /** Server-computed: hardcoded staff allowlist — always granted Global Mode. */
    is_staff_operator: z.boolean().optional(),
    /** Server-computed from the current Microsoft-authenticated session, when present. */
    microsoft_tenant_id: z.string().nullable().optional(),
});
// ── tenant activation (AIRA ↔ Sharefox Site) ───────────────────────────────
export const TenantActivationStatusSchema = z.enum(['inactive', 'activating', 'active']);
// ── staff / Global Mode operator APIs ───────────────────────────────────────
export const StaffProfileListItemSchema = z.object({
    id: Uuid,
    email: z.string().email().nullable(),
    full_name: z.string().nullable(),
    created_at: Iso,
    tenant_name: z.string(),
    sharefox_sitename: z.string().nullable(),
    sharefox_admin_user_id: OptionalIntFromDb.nullable(),
    auth_provider: ProfileAuthProviderSchema,
    global_access: z.boolean(),
});
export const StaffProfileListResponseSchema = z.object({
    items: z.array(StaffProfileListItemSchema),
});
export const StaffTenantListItemSchema = z.object({
    tenant_name: z.string(),
    type: z.string(),
    created_at: Iso,
    activation_status: TenantActivationStatusSchema,
    activated_at: Iso.nullable(),
    deletable: z.boolean(),
});
export const StaffTenantListResponseSchema = z.object({
    items: z.array(StaffTenantListItemSchema),
});
export const StaffTenantDeleteResponseSchema = z.object({
    ok: z.literal(true),
    tenant_name: z.string(),
    deleted: z.record(z.string(), z.number()),
    idp_claims_reset: z.number(),
    warnings: z.array(z.string()),
});
// ── GDPR DSAR (export / erasure) ────────────────────────────────────────────
export const GdprExportResponseSchema = z.object({
    ok: z.literal(true),
    exported_at: z.string(),
    tenant_name: z.string(),
    profile: z.record(z.string(), z.unknown()).nullable(),
    identity_platform_account: z.record(z.string(), z.unknown()).nullable().optional(),
    aira_runs: z.array(z.record(z.string(), z.unknown())),
    draft_history: z.array(z.record(z.string(), z.unknown())),
    // These collections were added after the original self-service export UI.
    // Keep them optional for a safe rolling deploy against an older backend,
    // while explicitly retaining them when Zod parses the response. Without
    // these keys Zod strips the expanded DSAR data before the browser download.
    aira_run_events: z.array(z.record(z.string(), z.unknown())).optional(),
    user_roles: z.array(z.record(z.string(), z.unknown())).optional(),
    token_usage_logs: z.array(z.record(z.string(), z.unknown())).optional(),
    processing_restrictions: z.array(z.record(z.string(), z.unknown())).optional(),
    gdpr_requests: z.array(z.record(z.string(), z.unknown())).optional(),
    langgraph_checkpoints: z.record(z.string(), z.unknown()).optional(),
    export_notes: z.record(z.string(), z.unknown()).optional(),
});
export const GdprErasureResponseSchema = z.object({
    ok: z.literal(true),
    user_id: Uuid,
    tenant_name: z.string(),
    deleted: z.record(z.string(), z.number()),
    idp_deleted: z.boolean(),
    warnings: z.array(z.string()),
});
export const OutlookPilotAccessStatusSchema = z.enum(['active', 'suspended']);
export const OutlookPilotAccessSchema = z.object({
    id: Uuid,
    microsoft_tenant_id: z.string().nullable(),
    tenant_display_name: z.string(),
    allowed_domains: z.array(z.string()),
    allowed_microsoft_user_ids: z.array(z.string()),
    status: OutlookPilotAccessStatusSchema,
    notes: z.string().nullable(),
    created_by: Uuid.nullable(),
    updated_by: Uuid.nullable(),
    created_at: Iso,
    updated_at: Iso,
});
export const OutlookPilotAccessListResponseSchema = z.object({
    items: z.array(OutlookPilotAccessSchema),
});
// ── draft readiness ─────────────────────────────────────────────────────────
/** Stable, machine-readable reasons a profile is not Gmail-draft-ready. */
export const DraftReadinessReasonSchema = z.enum([
    'missing_sharefox_sitename',
    'missing_integration_token',
    'tenant_inactive',
    'duplicate_profile_conflict',
]);
export const DraftReadinessSchema = z.object({
    profile_id: Uuid,
    email: z.string().nullable(),
    tenant_name: z.string().nullable(),
    sharefox_sitename: z.string().nullable(),
    activation_status: z.string().nullable(),
    has_integration_token: z.boolean(),
    duplicate_profiles: z.array(z.object({
        id: Uuid,
        tenant_name: z.string().nullable(),
        sharefox_sitename: z.string().nullable(),
    })),
    duplicate_profile_conflict: z.boolean(),
    draft_ready: z.boolean(),
    blocking_reasons: z.array(DraftReadinessReasonSchema),
    reasons: z.array(DraftReadinessReasonSchema),
});
/** Human-readable copy for each readiness reason — shared so backend logs and UI agree. */
export const DRAFT_READINESS_REASON_LABEL = {
    missing_sharefox_sitename: 'No Sharefox site bound — re-activate from Sharefox Admin.',
    missing_integration_token: 'Sharefox integration token missing or expired.',
    tenant_inactive: 'Tenant is not active.',
    duplicate_profile_conflict: 'Duplicate profiles for this email are bound to different sites.',
};
export const StaffProfileResetTenantResponseSchema = z.object({
    ok: z.literal(true),
    already_unbound: z.boolean(),
    profile: StaffProfileListItemSchema,
    previous: z.object({
        tenant_name: z.string(),
        sharefox_sitename: z.string().nullable(),
    }),
    warnings: z.array(z.string()),
});
export const StaffProfileUpdateResponseSchema = z.object({
    ok: z.literal(true),
    profile: StaffProfileListItemSchema,
});
export const TenantActivationStateSchema = z.object({
    needs_activation: z.boolean(),
    tenant_name: z.string().nullable(),
    sharefox_sitename: z.string().nullable().optional(),
    activation_status: TenantActivationStatusSchema.nullable(),
    activated_at: Iso.nullable(),
    site_display_name: z.string().nullable(),
});
export const TenantActivateRequestSchema = z.object({
    site_name: z
        .string()
        .min(1)
        .max(63)
        .regex(/^[a-z0-9_-]+$/i, 'site_name must be alphanumeric, underscore, or dash')
        .optional(),
    /** Sharefox Admin user session from embed — authorizes /admin/aira/activate (stub or prod). */
    sharefox_user_token: z.string().min(1).optional(),
});
export const TenantActivateOnboardingSchema = z.object({
    is_new_tenant: z.literal(true),
    shop_url: z.string().url(),
    sitemap_url: z.string().url(),
});
export const TenantActivateResponseSchema = z.object({
    /**
     * `true` when the tenant is fully active (existing-tenant re-activation, which
     * is synchronous). `false` when a brand-new tenant has entered the server-side
     * onboarding flow — the tenant stays in `activating` until the backend job
     * (config → crawl → agent) finishes, observed over the onboarding SSE stream.
     */
    activated: z.boolean(),
    tenant_name: z.string(),
    activation_status: TenantActivationStatusSchema,
    activated_at: Iso.nullable(),
    /** Present for brand-new tenants — the SPA switches to the progress view. */
    onboarding: TenantActivateOnboardingSchema.nullable().optional(),
    /** False for AIRA-only demo tenants activated without a Sharefox integration. */
    sharefox_connected: z.boolean().optional(),
    /** Human-readable note when sharefox_connected is false. */
    message: z.string().optional(),
    /**
     * Present on synchronous re-activation — the post-activation readiness of the
     * exact profile that was bound, so the SPA can warn if drafting is still
     * blocked despite a successful activation.
     */
    readiness: DraftReadinessSchema.nullable().optional(),
});
// ── tenant onboarding progress (server-driven, polled via apiClient) ────────
export const OnboardingStepStatusSchema = z.enum([
    'pending',
    'running',
    'completed',
    'failed',
    'skipped',
]);
export const OnboardingStatusSchema = z.enum(['running', 'completed', 'failed']);
export const OnboardingPageSyncResultSchema = z.object({
    pageUrl: z.string(),
    status: z.enum(['pending', 'processing', 'success', 'error']),
    entriesCreated: z.number(),
    error: z.string().optional(),
});
export const OnboardingSyncSummarySchema = z.object({
    pagesProcessed: z.number(),
    pagesSucceeded: z.number(),
    pagesFailed: z.number(),
    totalEntriesCreated: z.number(),
});
export const OnboardingProgressSchema = z.object({
    status: OnboardingStatusSchema,
    steps: z.object({
        config: OnboardingStepStatusSchema,
        crawl: OnboardingStepStatusSchema,
        agent: OnboardingStepStatusSchema,
    }),
    step_errors: z.record(z.string()).default({}),
    crawl: z.object({
        seed_url: z.string().nullable(),
        pages: z.array(OnboardingPageSyncResultSchema),
        summary: OnboardingSyncSummarySchema.nullable(),
    }),
});
//# sourceMappingURL=email-resources.js.map